import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { useEffect, useState } from "react";
import TableWallet from "../../walletReports/table";
import StackKit from "@/shared/components/kit/stack";
import OnlinePayment from "./onlinePayment";
import CreditPayment from "./CreditPayment";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchInstallments, insBalancePay, paymentRequest } from "@/api/user/home";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";
import moment from "jalali-moment";
import CardBoxLayout from "@/modules/user/layout/cardBoxLayout";
import useAccountStore from "@/store/balanceStore";

/**
 * SelectedRowDetail
 *
 * نمایش جزئیات اقساط یک وام، امکان پرداخت آنلاین و پرداخت از کیف پول،
 * و مدیریت مودال‌ها، متد پرداخت، و بارگذاری مجدد اطلاعات پس از پرداخت.
 *
 * @param dataProps اطلاعات ردیف انتخاب‌شده (اختیاری)
 * @param onBack تابع بازگشت به صفحه قبل
 *
 * @returns JSX.Element
 */

/**
 * Converts a unix timestamp (in seconds) to a Jalali date string.
 * Returns "—" for invalid, null, undefined, empty, or non-positive values
 * (including "0", 0, null, undefined, NaN).
 */
const toJalaliDate = (ts: any): string => {
    if (ts === null || ts === undefined || ts === "") return "—";
    const num = Number(ts);
    if (isNaN(num) || num <= 0) return "—";
    return moment(num * 1000).locale("fa").format("YYYY/MM/DD");
};

const SelectedRowDetail = ({
                               dataProps,
                               onBack,
                           }: {
    dataProps?: any;
    onBack?: () => void;
}) => {
    const [modalPaymentCash, setModalPaymentCash] = useState(false);
    const [modalCredit, setModalCredit] = useState(false);
    const [selectedCard, setSelectedCard] = useState<string>("");
    const [selectedInstallment, setSelectedInstallment] = useState<any>(null);
    const { balance } = useAccountStore();
    const { id } = useParams();
    const [loanID, setLoanID] = useState<number>(0);

    useEffect(() => {
        setLoanID(Number(id));
    }, [id]);

    const { data } = useQuery({
        queryKey: ["storePoint-data", loanID],
        queryFn: () => fetchInstallments(loanID),
    });

    const installments = data?.data?.installments || [];

    // === API CALLS ===
    const onPaymentSubmit = async (
        installmentId: number,
        loanId: number,
        amount: number,
        gateway: string
    ) => {
        try {
            const paymentData = { installmentId, loanId, gateway, amount };
            await paymentRequest(paymentData);
        } catch (error) {
            console.error("Payment failed:", error);
        }
    };

    const insBalancePaySubmit = async (id: number, amount: number) => {
        try {
            const nsBalancePayata = { id, amount };
            await insBalancePay(nsBalancePayata);
        } catch (error) {
            console.error("Credit payment failed:", error);
        }
    };

    const dataHeader = [
        { id: "ts", title: "تاریخ سررسید" },
        { id: "sum", title: "مبلغ پرداختی هر قسط" },
        { id: "pay_ts", title: "تاریخ پرداخت قسط" },
        { id: "status", title: "وضعیت تراکنش" },
        { id: "res", title: "کد رهگیری تراکنش" },
        { id: "moreElement", title: "نحوه پرداخت" },
    ];

    const newDataInstallment = installments.map((item: any, index: number) => {
        const isPaid = item.status === 'payed';
        const isCanceled = item.status === "canceled";

        const payDateRaw = toJalaliDate(item.pay_ts);
        const dueDateRaw = toJalaliDate(item.ts);

        const payDate = payDateRaw === "—" ? "—" : toPersianNumber(payDateRaw);
        const dueDate = dueDateRaw === "—" ? "—" : toPersianNumber(dueDateRaw);

        return {
            ...item,
            rowId: toPersianNumber(index + 1),
            sum: numberSeparator(item.sum),
            pay_ts: payDate,
            ts: dueDate,
            status:
                item?.status === "open" ? (
                    <div className="px-2 py-1 rounded-full bg-yellow-500/25 text-yellow-500 border-1 border-yellow-500">
                        باز
                    </div>
                ) : item?.status === "payed" ? (
                    <div className="px-2 py-1 rounded-full bg-green-500/25 text-green-500 border-1 border-green-500">
                        پرداخت شده
                    </div>
                ) : (
                    <div className="px-2 py-1 rounded-full bg-red-500/25 text-red-500 border-1 border-red-500">
                        لغو شده
                    </div>
                ),
            moreElement: (
                <StackKit
                    direction="row"
                    gap={1}
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <CustomButton
                        onClick={() => {
                            setSelectedInstallment(item);
                            setModalPaymentCash(true);
                        }}
                        disabled={isPaid || isCanceled}
                    >
                        <TypoGraphyKit variant="body1" sx={{ fontSize: "12px" }}>
                            پرداخت آنلاین
                        </TypoGraphyKit>
                    </CustomButton>

                    <CustomButton
                        onClick={() => {
                            setSelectedInstallment(item);
                            setModalCredit(true);
                        }}
                        disabled={isPaid || isCanceled}
                    >
                        <TypoGraphyKit variant="body1" sx={{ fontSize: "12px" }}>
                            پرداخت کیف پول
                        </TypoGraphyKit>
                    </CustomButton>
                </StackKit>
            ),
            res: item.res ? toPersianNumber(item.res) : "—",
        };
    });

    return (
        <CardBoxLayout>
            <BoxKit sx={{ padding: 2, color: "#fff" }}>
                <TableWallet dataHeader={dataHeader} dataTbody={newDataInstallment} />

                <StackKit direction="row" justifyContent="space-between" gap={3}>
                    <TypoGraphyKit>
                        مجموع اصل و سود کل اقساط باقیمانده:{" "}
                        {numberSeparator(data?.data.totalAmountOpen)} ریال
                    </TypoGraphyKit>
                    <TypoGraphyKit>
                        تخفیف در صورت پرداخت یکجا:{" "}
                        {numberSeparator(data?.data.clearingDiscount)} ریال
                    </TypoGraphyKit>
                    <CustomButton
                        onClick={onBack}
                        sx={{
                            marginTop: 2,
                            color: "#fff",
                            borderColor: "#fff",
                            border: "none",
                        }}
                        variant="outlined"
                        // disabled={data?.data?.totalAmountOpen === 0}
                        disabled={true}
                    >
                        پرداخت باقی مانده اقساط {numberSeparator(data?.data?.delayCount)}{" "}
                        قسط: {numberSeparator(data?.data?.totalAmountOpen)} ریال
                    </CustomButton>
                </StackKit>

                {/* Online Payment */}
                <OnlinePayment
                    setModalPaymentCash={setModalPaymentCash}
                    modalPaymentCash={modalPaymentCash}
                    selectedCard={selectedCard}
                    loanId={loanID}
                    installmentId={selectedInstallment?.id || 0}
                    paymentAmount={selectedInstallment?.sum || 0}
                    onPaymentSubmit={onPaymentSubmit}
                />

                {/* Credit Payment */}
                <CreditPayment
                    modalCredit={modalCredit}
                    setModalCredit={setModalCredit}
                    loanId={loanID}
                    installmentId={selectedInstallment?.id || 0}
                    paymentAmount={selectedInstallment?.sum || 0}
                    insBalancePay={insBalancePaySubmit}
                    walletBalance={balance}
                />
            </BoxKit>
        </CardBoxLayout>
    );
};

export default SelectedRowDetail;